import React, { ChangeEvent, useState } from "react";

import { ListItemIcon } from "@material-ui/core";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { useSelector } from "react-redux";
import { openEditBusinessModal } from "lib/navigation";
import { BusinessModalSection } from "constants/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { ClickableLink } from "components/Link";
import {
  Label,
  StyledCheckbox,
  StyledInput,
} from "components/Form/FormikControls";
import { EmailRecipientIconList } from "components/Icons/EmailRecipientIconList";
import { Column, Row } from "components/Layout";

import { getBillingDocumentsAggridDataById } from "selectors";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { SlimButton } from "components/Button";
import { EMPTY_ARRAY } from "lib/index";

interface BusinessUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

interface ToggleRecipientAction {
  billingDocumentId: string;
  businessUser?: BusinessUser;
  emailRecipient?: EmailRecipient;
  rawEmailAddress?: string;
}

interface ToggleRecipientState {
  businessUsers: BusinessUser[];
  emailRecipients: EmailRecipient[];
  rawEmailAddress: string;
}

interface RecipientListProps {
  billingDocumentId: string;
  toggleRecipientAction: (ToggleRecipientAction) => void;
  toggleRecipientState: ToggleRecipientState;
}

interface RecipientComponentProps {
  recipient: BusinessUser | EmailRecipient;
  selectedIds: number[];
  toggleRecipientAction: (action: ToggleRecipientAction) => void;
  type: "businessUser" | "emailRecipient";
  billingDocumentId: string;
}

const RecipientComponent = ({
  recipient,
  selectedIds,
  toggleRecipientAction,
  type,
  billingDocumentId,
}: RecipientComponentProps): React.ReactElement => {
  const checked = selectedIds.includes(recipient.id);
  const onClick = () =>
    toggleRecipientAction({
      billingDocumentId,
      [type]: recipient,
    });

  const labelId = `checkbox-list-label-${billingDocumentId}`;

  const recipientText = (
    <Row alignCenter justifyBetween>
      {recipient.firstName} {recipient.lastName}: {recipient.email}{" "}
      <EmailRecipientIconList recipient={recipient} />
    </Row>
  );

  return (
    <ListItem key={recipient.id} dense disableGutters button onClick={onClick}>
      <ListItemIcon>
        <StyledCheckbox
          edge="start"
          checked={!!checked}
          tabIndex={-1}
          disableRipple
          inputProps={{ "aria-labelledby": labelId }}
        />
      </ListItemIcon>
      <ListItemText id={labelId} primary={recipientText} />
    </ListItem>
  );
};

const RecipientListComponent = ({
  billingDocumentId,
  toggleRecipientAction,
  toggleRecipientState,
}: RecipientListProps): React.ReactElement => {
  const { billingDocument, business } = useSelector(
    getBillingDocumentsAggridDataById(billingDocumentId),
  );
  // Flatten the email recipients into just their ids.
  // (We need it to be not be flat because we need the source (Deployment or Saleyard) aswell as the id.)
  const selectedEmailRecipientIds =
    toggleRecipientState.emailRecipients?.map(er => er.id) || EMPTY_ARRAY;

  const selectedBusinessUserIds =
    toggleRecipientState.businessUsers?.map(er => er.id) || EMPTY_ARRAY;

  // Default to showing only the sub-list if there are any selected on render.
  const anySelected =
    selectedBusinessUserIds.length > 0 || selectedEmailRecipientIds.length > 0;

  const [showAll, setShowAll] = useState(false);
  const toggleShowAll = () => setShowAll(!showAll);

  function onClickEditBusiness() {
    openEditBusinessModal(
      business.id,
      undefined,
      BusinessModalSection.EMAIL_RECIPIENTS,
    );
  }

  const emailRecipientsWithEmails =
    business.emailRecipients?.filter(er => er.email) || EMPTY_ARRAY;

  // flatten own email recipients to just their emails
  const emailRecipientEmails =
    emailRecipientsWithEmails.map(er => er.email) || EMPTY_ARRAY;
  const businessUsers =
    business.businessUsers?.filter(
      bu => !emailRecipientEmails.includes(bu.email),
    ) || EMPTY_ARRAY;

  return (
    <React.Fragment key={billingDocumentId}>
      <Row justifyBetween>
        <div>
          <strong>
            {billingDocument.type}
            {billingDocument.number}
          </strong>
        </div>
        <div>
          <ClickableLink onClick={onClickEditBusiness}>
            <FontAwesomeIcon icon={faPencil} />
            &nbsp;<strong>{business.name}</strong>{" "}
          </ClickableLink>
        </div>
      </Row>
      <List dense>
        {/* Show selected entries by default.  */}
        {!anySelected && <p>No recipients selected.</p>}
        {businessUsers
          .filter(bu => selectedBusinessUserIds.includes(bu.id))
          .map(bu => (
            <RecipientComponent
              key={bu.id}
              recipient={bu}
              selectedIds={selectedBusinessUserIds}
              toggleRecipientAction={toggleRecipientAction}
              billingDocumentId={billingDocumentId}
              type="businessUser"
            />
          ))}

        {emailRecipientsWithEmails
          .filter(er => selectedEmailRecipientIds.includes(er.id))
          .map(er => (
            <RecipientComponent
              key={er.id}
              recipient={er}
              selectedIds={selectedEmailRecipientIds}
              toggleRecipientAction={toggleRecipientAction}
              billingDocumentId={billingDocumentId}
              type="emailRecipient"
            />
          ))}
      </List>

      <Row fullWidth justifyEnd>
        <SlimButton type="button" onClick={toggleShowAll}>
          <ButtonIcon icon={showAll ? faMinus : faPlus} />
          &nbsp;{showAll ? "Show less" : "Show more"}
        </SlimButton>
      </Row>

      {/* Then the rest.  */}
      {showAll && (
        <>
          <List>
            {businessUsers
              .filter(bu => !selectedBusinessUserIds.includes(bu.id))
              .map(bu => (
                <RecipientComponent
                  key={bu.id}
                  recipient={bu}
                  selectedIds={selectedBusinessUserIds}
                  toggleRecipientAction={toggleRecipientAction}
                  billingDocumentId={billingDocumentId}
                  type="businessUser"
                />
              ))}

            {emailRecipientsWithEmails
              .filter(er => !selectedEmailRecipientIds.includes(er.id))
              .map(er => (
                <RecipientComponent
                  key={er.id}
                  recipient={er}
                  selectedIds={selectedEmailRecipientIds}
                  toggleRecipientAction={toggleRecipientAction}
                  billingDocumentId={billingDocumentId}
                  type="emailRecipient"
                />
              ))}
          </List>
          <Column>
            <Label tooltip="Send to specific email address.">
              Email address
            </Label>
            <StyledInput
              type="email"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                toggleRecipientAction({
                  billingDocumentId,
                  rawEmailAddress: e.target.value,
                })
              }
            />
          </Column>
        </>
      )}

      <hr />
    </React.Fragment>
  );
};

export const RecipientList = React.memo(RecipientListComponent);
